import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Badge } from '../components/ui/badge';
import { MapPin, Calendar } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { key: 'all', de: 'Alle', en: 'All', es: 'Todos' },
  { key: 'hallenbau', de: 'Hallenbau', en: 'Hall Construction', es: 'Construcción de Naves' },
];

export default function ProjectsPage() {
  const { t, language } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    axios.get(`${API}/projects`).then(r => setProjects(r.data)).catch(() => {});
  }, []);

  const getLocalized = (item, field) => item[`${field}_${language}`] || item[`${field}_de`] || '';

  const filtered = filter === 'all' ? projects : projects.filter(p => p.category === filter);

  return (
    <div data-testid="projects-page">
      <SEO page="projects" />
      {/* Hero */}
      <section className="relative py-20 md:py-28 bg-[#0F172A]">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px'}} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 text-center">
          <span className="inline-block text-[#3b8dff] text-sm font-semibold uppercase tracking-wider mb-4">{t('projects.tag')}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6" data-testid="projects-title">{t('projects.title')}</h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">{t('projects.subtitle')}</p>
        </div>
      </section>

      {/* Projects */}
      <section className="py-20 md:py-32 blueprint-bg">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-3 mb-12" data-testid="project-filters">
            {categories.map(cat => (
              <button
                key={cat.key}
                onClick={() => setFilter(cat.key)}
                data-testid={`filter-${cat.key}`}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  filter === cat.key
                    ? 'bg-[#066ce7] text-white shadow-lg'
                    : 'bg-white text-slate-600 hover:text-[#066ce7] border border-slate-200'
                }`}
              >
                {cat[language] || cat.de}
              </button>
            ))}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((project, i) => (
              <div key={project.id || i} className="relative aspect-[4/3] rounded-xl overflow-hidden group cursor-pointer shadow-lg hover:shadow-2xl transition-all" data-testid={`project-card-${i}`}>
                <img
                  src={project.images?.[0] || '/api/uploads/hero_main.jpg'}
                  alt={getLocalized(project, 'title')}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="project-overlay absolute inset-0 flex flex-col justify-end p-6">
                  <Badge className="bg-[#066ce7] text-white w-fit mb-2 text-xs">{project.category}</Badge>
                  <h3 className="text-white text-lg font-bold mb-2">{getLocalized(project, 'title')}</h3>
                  <p className="text-white/70 text-sm mb-3 line-clamp-2">{getLocalized(project, 'description')}</p>
                  <div className="flex items-center gap-4 text-white/60 text-xs">
                    {project.location && (
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{project.location}</span>
                    )}
                    {project.year && (
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{project.year}</span>
                    )}
                    {project.area && (
                      <span className="font-mono">{project.area}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filtered.length === 0 && (
            <p className="text-center text-slate-500 py-20">
              {language === 'de' ? 'Keine Projekte in dieser Kategorie.' : language === 'en' ? 'No projects in this category.' : 'No hay proyectos en esta categoría.'}
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
